import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, MapPin, Phone, Mail, Globe, Clock, UtensilsCrossed, Navigation, ShieldAlert, Database } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { StateBox, OsmAttribution } from "../components/directory/DirectoryStates";
import { SECTIONS, directoryApi, formatAddress, websiteHref } from "../lib/directory";

const Row = ({ icon: Icon, label, id, children }) => {
  const { t } = useLang();
  const empty = children == null || children === "" || (Array.isArray(children) && children.length === 0);
  return (
    <div className="flex gap-3 py-4 border-b border-white/[0.06] last:border-0" data-testid={`profile-row-${id}`}>
      <Icon size={17} className="text-gold/80 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.15em] text-white/40">{label}</div>
        {empty
          ? <div className="text-sm italic text-white/35 mt-0.5" data-testid={`profile-${id}-unavailable`}>{t("directory.not_available")}</div>
          : <div className="text-sm text-white/85 mt-0.5 break-words" data-testid={`profile-${id}-value`}>{children}</div>}
      </div>
    </div>
  );
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

function Details({ b }) {
  const { t } = useLang();
  const phones = b.phone ? b.phone.split(";").map((p) => p.trim()).filter(Boolean) : [];
  const address = formatAddress(b.address);
  return (
    <div className="rounded-2xl bg-surface/60 border border-white/[0.06] px-6" data-testid="profile-details">
      <Row icon={MapPin} id="address" label={t("directory.address")}>{address || null}</Row>
      <Row icon={Navigation} id="neighborhood" label={t("directory.neighborhood")}>
        {b.neighborhood_name
          ? <>{b.neighborhood_name}{b.neighborhood_sub_area && <span data-testid="profile-neighborhood-subarea"> · {b.neighborhood_sub_area}</span>}<span className="text-white/40"> · {b.neighborhood_source === "manual" ? t("directory.nb_manual") : t("directory.nb_boundary")}</span></>
          : <span className="italic text-white/45" data-testid="profile-neighborhood-unverified">{t("directory.location_unavailable")}</span>}
      </Row>
      <Row icon={Phone} id="phone" label={t("directory.phone")}>
        {phones.length ? phones.map((p) => <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="block hover:text-gold">{p}</a>) : null}
      </Row>
      <Row icon={Mail} id="email" label={t("directory.email")}>{b.email ? <a href={`mailto:${b.email}`} className="hover:text-gold">{b.email}</a> : null}</Row>
      <Row icon={Globe} id="website" label={t("directory.website")}>
        {b.website ? <a href={websiteHref(b.website)} target="_blank" rel="noopener noreferrer nofollow" className="text-gold/90 hover:text-gold underline underline-offset-2">{b.website}</a> : null}
      </Row>
      <Row icon={Clock} id="hours" label={t("directory.hours")}>
        {b.opening_hours ? <>{b.opening_hours} <span className="text-white/40">· {t("directory.as_listed")}</span></> : null}
      </Row>
      {b.section === "restaurants" && (
        <Row icon={UtensilsCrossed} id="cuisine" label={t("directory.cuisine")}>{b.cuisine.length ? <span className="capitalize">{b.cuisine.join(", ")}</span> : null}</Row>
      )}
    </div>
  );
}

function SourcePanel({ b }) {
  const { t } = useLang();
  return (
    <aside className="rounded-2xl glass p-6 space-y-4" data-testid="profile-source-panel">
      <div className="flex items-start gap-2.5 text-sm text-amber-200/85" data-testid="profile-verification-status">
        <ShieldAlert size={16} className="shrink-0 mt-0.5" />
        <span>{b.verification_status === "verified" ? t("directory.verified") : t("directory.unverified")}</span>
      </div>
      <div className="text-sm text-white/60">
        <div className="flex items-center gap-2 text-white/80"><Database size={14} className="text-gold/80" /> {t("directory.source")}</div>
        <div className="mt-2 space-y-1 text-xs">
          <div>{t("directory.first_imported")}: <span className="text-white/80" data-testid="profile-first-imported">{fmtDate(b.first_imported_at)}</span></div>
          <div>{t("directory.last_seen")}: <span className="text-white/80" data-testid="profile-last-seen">{fmtDate(b.last_seen_at)}</span></div>
        </div>
        <a href={b.source.element_url} target="_blank" rel="noopener noreferrer" data-testid="profile-osm-element-link"
          className="inline-block mt-3 text-xs text-gold/85 hover:text-gold underline underline-offset-2">{t("directory.view_osm_record")}</a>
      </div>
      <a href={b.map_url} target="_blank" rel="noopener noreferrer" data-testid="profile-map-link"
        className="flex items-center justify-center gap-1.5 w-full px-4 py-2.5 rounded-full bg-gold text-ink text-sm font-semibold hover:brightness-110 transition-[filter] duration-200">
        <MapPin size={14} /> {t("directory.view_map")}
      </a>
      <OsmAttribution />
    </aside>
  );
}

export default function BusinessProfile({ section }) {
  const { slug } = useParams();
  const { t } = useLang();
  const cfg = SECTIONS[section];
  const backTo = `${cfg.path}${useLocation().state?.fromSearch || ""}`;
  const [state, setState] = useState({ status: "loading", b: null });

  const load = useCallback(() => {
    setState({ status: "loading", b: null });
    directoryApi.business(slug)
      .then((b) => setState({ status: b.section === section ? "ok" : "notfound", b }))
      .catch((e) => setState({ status: e?.response?.status === 404 ? "notfound" : "error", b: null }));
  }, [slug, section]);
  useEffect(load, [load]);

  const { b, status } = state;
  return (
    <div className="max-w-[1100px] mx-auto px-5 lg:px-8 py-12" data-testid="business-profile-page">
      <Link to={backTo} data-testid="profile-back-link" className="inline-flex items-center gap-1.5 text-sm text-white/55 hover:text-gold transition-colors duration-200">
        <ArrowLeft size={15} className="rtl:rotate-180" /> {t(`directory.sections.${section}.title`)}
      </Link>
      {status === "loading" && <div className="mt-8"><StateBox kind="loading" title={t("directory.loading")} /></div>}
      {status === "error" && <div className="mt-8"><StateBox kind="error" title={t("directory.error_h")} body={t("directory.error_p")} onRetry={load} /></div>}
      {status === "notfound" && <div className="mt-8"><StateBox kind="empty" title={t("directory.notfound_h")} body={t("directory.notfound_p")} /></div>}
      {status === "ok" && (
        <>
          <Helmet>
            <title>{`${b.name} — ${b.category_name || ""} in Dubai | BuyDubai.ai`}</title>
            <meta name="description" content={`${b.name}${b.neighborhood_name ? `, ${b.neighborhood_name}` : ""}, Dubai. Listing sourced from OpenStreetMap.`} />
            <meta property="og:title" content={`${b.name} | BuyDubai.ai`} />
          </Helmet>
          <div className="mt-6">
            <div className="text-xs uppercase tracking-[0.2em] text-gold/80" data-testid="profile-category">{b.category_name}</div>
            <h1 className="font-display text-4xl sm:text-5xl text-white mt-2" data-testid="profile-name">{b.name}</h1>
            {b.name_ar && <div className="text-white/50 mt-1" dir="rtl" lang="ar" data-testid="profile-name-ar">{b.name_ar}</div>}
            {b.brand && b.brand !== b.name && <div className="text-sm text-white/50 mt-1">{t("directory.brand")}: {b.brand}</div>}
          </div>
          <div className="grid lg:grid-cols-3 gap-6 mt-8">
            <div className="lg:col-span-2"><Details b={b} /></div>
            <SourcePanel b={b} />
          </div>
        </>
      )}
    </div>
  );
}
